import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { 
  Cloud, 
  CloudOff, 
  AlertTriangle, 
  CheckCircle, 
  RefreshCw, 
  ArrowLeft,
  Loader2,
  Upload,
  FileWarning
} from 'lucide-react';

const CloudinaryMigration = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [migrating, setMigrating] = useState({});
  const [migratingAll, setMigratingAll] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/books/cloudinary-status`);
      setData(response.data);
    } catch (error) {
      toast.error('Kon nie migrasie status laai nie');
      console.error('Error fetching cloudinary status:', error);
    } finally {
      setLoading(false);
    }
  };

  const migrateBook = async (bookId, bookName) => {
    setMigrating(prev => ({ ...prev, [bookId]: true }));
    try {
      const response = await axios.post(`${API}/admin/books/${bookId}/migrate-to-cloudinary`);
      if (response.data.already_migrated) {
        toast.info(`${bookName} is reeds in Cloudinary`);
      } else {
        toast.success(`${bookName} suksesvol gemigreer!`);
      }
      // Refresh the list
      await fetchStatus();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Migrasie het misluk';
      toast.error(`${bookName}: ${errorMsg}`);
    } finally {
      setMigrating(prev => ({ ...prev, [bookId]: false }));
    }
  };

  const migrateAllBooks = async () => {
    if (!data?.needs_migration?.length) return;
    
    setMigratingAll(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const book of data.needs_migration) {
      try {
        await axios.post(`${API}/admin/books/${book.id}/migrate-to-cloudinary`);
        successCount++;
      } catch (error) {
        failCount++;
        console.error(`Failed to migrate ${book.boek_naam}:`, error);
      }
    }
    
    toast.success(`Migrasie voltooi: ${successCount} suksesvol, ${failCount} misluk`);
    await fetchStatus();
    setMigratingAll(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const summary = data?.summary || {};
  const totalBooks = summary.total_books || 0;
  const cloudinaryOk = summary.cloudinary_ok || 0;
  const needsMigration = summary.needs_migration || 0;
  const noEpub = summary.no_epub || 0;
  const healthPercentage = totalBooks > 0 ? Math.round((cloudinaryOk / totalBooks) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/books')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Terug
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Cloudinary Migrasie</h1>
            <p className="text-sm text-gray-500">Bestuur boek lêer berging vir permanente stoor</p>
          </div>
        </div>
        <Button onClick={fetchStatus} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Verfris
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Cloud className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Totaal Boeke</p>
                <p className="text-2xl font-bold">{totalBooks}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">In Cloudinary</p>
                <p className="text-2xl font-bold text-green-600">{cloudinaryOk}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Benodig Migrasie</p>
                <p className="text-2xl font-bold text-yellow-600">{needsMigration}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <CloudOff className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Geen EPUB</p>
                <p className="text-2xl font-bold text-red-600">{noEpub}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Health Progress */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Cloudinary Gesondheid</span>
              <span className="font-medium">{healthPercentage}%</span>
            </div>
            <Progress value={healthPercentage} className="h-3" />
            <p className="text-xs text-gray-500">
              {cloudinaryOk} van {totalBooks} boeke is veilig in Cloudinary gestoor
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Books Needing Migration */}
      {data?.needs_migration?.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  Boeke wat Migrasie Benodig
                </CardTitle>
                <CardDescription>
                  Hierdie boeke het lokale lêers wat verlore sal gaan met herinstallering
                </CardDescription>
              </div>
              <Button 
                onClick={migrateAllBooks}
                disabled={migratingAll}
                className="bg-yellow-500 hover:bg-yellow-600"
              >
                {migratingAll ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Migreer...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Migreer Alles ({needsMigration})
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.needs_migration.map(book => (
                <div 
                  key={book.id} 
                  className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200"
                >
                  <div>
                    <p className="font-medium text-gray-900">{book.boek_naam}</p>
                    <p className="text-sm text-gray-500">deur {book.skrywer_naam}</p>
                    <div className="flex gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{book.status}</span>
                      {book.can_regenerate && (
                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                          Kan hergenereer
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => migrateBook(book.id, book.boek_naam)}
                    disabled={migrating[book.id] || migratingAll}
                    variant="outline"
                  >
                    {migrating[book.id] ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Cloud className="h-4 w-4 mr-1" />
                        Migreer
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Books Without EPUB */}
      {data?.no_epub?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileWarning className="h-5 w-5 text-red-500" />
              Boeke Sonder EPUB
            </CardTitle>
            <CardDescription>
              Hierdie boeke het geen EPUB lêer nie en moet handmatig opgelaai word
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.no_epub.map(book => (
                <div 
                  key={book.id} 
                  className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200"
                >
                  <div>
                    <p className="font-medium text-gray-900">{book.boek_naam}</p>
                    <p className="text-sm text-gray-500">deur {book.skrywer_naam}</p>
                    <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{book.status}</span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/admin/publication-preview/${book.id}`)}
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    Laai Op
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Books in Cloudinary (Collapsible) */}
      {data?.cloudinary_ok?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Boeke Veilig in Cloudinary ({cloudinaryOk})
            </CardTitle>
            <CardDescription>
              Hierdie boeke se EPUB lêers is permanent gestoor
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {data.cloudinary_ok.slice(0, 12).map(book => (
                <div 
                  key={book.id} 
                  className="p-2 bg-green-50 rounded border border-green-200 text-sm"
                >
                  <p className="font-medium text-gray-900 truncate">{book.boek_naam}</p>
                  <p className="text-xs text-gray-500 truncate">deur {book.skrywer_naam}</p>
                </div>
              ))}
              {data.cloudinary_ok.length > 12 && (
                <div className="p-2 bg-gray-50 rounded border text-sm flex items-center justify-center text-gray-500">
                  +{data.cloudinary_ok.length - 12} meer boeke
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CloudinaryMigration;
